import { Dayjs } from "dayjs";

export interface IPrelimApplicationData {
  id: Number | undefined,
  status: String | undefined,
  /** Admin who forwarded for approval; cannot be the same user who approves. */
  reviewedByUserId: Number | undefined,
  assignedCheckerUserId?: Number | undefined,
  assignedMakerUserId?: Number | undefined,
  /** Screening committee / manager assignee (API may also send as assignedUserAdminUserId). */
  assignedManagerUserId?: Number | undefined,
  assignedUserAdminUserId?: Number | undefined,
  assignedPfUserId?: Number | undefined,
  sanctionedAmountInr?: string | undefined,
  createdBy: Number | undefined,
  createdByName: String | undefined,
  parentId: Number | undefined,
  createdOn: String | undefined,
  detailedApplicationCreatedOn: String | undefined,
  applicationSubmissionDate: String | undefined,
  detailedApplicationSubmissionDate: String | undefined,
  updatedBy: String | undefined,
  updatedOn: String | undefined,
  scheme: String | undefined,
  nameOfTheFund: String | undefined,
  sponsor: String | undefined,
  investmentManager: String | undefined,
  fundManager: String | undefined,
  dealType: Number | undefined,
  impact: Number | undefined,
  aifCategory: String | undefined,
  nameOfTrustee: String | undefined,
  dateOfFilingWithSEBI: Dayjs | null,
  contributionSought: Number | undefined,
  termOfFund: Number | undefined,
  commitmentPeriod: Number | undefined,
  preferredReturn: Number | undefined,
  managementFees: Number | undefined,
  carriedInterest: Number | undefined,
  dealSector: String | undefined,
  dealSubsector: String | undefined,
  description: String | undefined,
  investmentStrategy: String | undefined,
  sdPvtPlacementMemorandum: String | undefined,
  sdImAgreement: String | undefined,
  sdTrustDeal: String | undefined,
  sdSEBICertificate: String | undefined,
  sdDescription: String | undefined,
  sdTargetCorpusDomestic: Number | undefined,
  sdTargetCorpusOverseas: Number | undefined,
  sdTotalTargetCorpus: Number | undefined,
  sdGreenShoeTargetCorpusDomestic: Number | undefined,
  sdGreenShoeTargetCorpusOverseas: Number | undefined,
  sdGreenShoeTotalTargetCorpus: Number | undefined,
  sdFirstClosingDomesticAmount: Number | undefined,
  sdFirstClosingDomesticAmountDate: Dayjs | null,
  sdFirstClosingOverseasAmount: Number | undefined,
  sdFirstCorpusOverseasAmountDate: String | undefined,
  investmentTrackRecord: String | undefined,
  declarationAccepted: boolean,
  ndaAccepted: boolean,
  firstClosing: boolean,
  hasInvestment: boolean,
  stage: String | undefined,
  detailedApplicationId: Number | undefined,
  actionDate?: Date,
  aifCategoryType: String | undefined,
  targetReturnIRR: Number | undefined,
  fundSetupCost: Number | undefined,
  hurdleCarryInterestRate: Number | undefined,
  justificationForHurdleCarryInterestRate: String | undefined,
  otherExpenses: Number | undefined,
  // Investment Strategy
  isStrategyBasis: String | undefined,
  isFocusSectors: String | undefined,
  isComparisonPast: String | undefined,
  isSignificantChange: String | undefined,
  isSectorSituations: String | undefined,
  isControlsRights: String | undefined,
  isRightsProtections: String | undefined,
  isInvestmentPolicy: String | undefined,
  isRisksMitigation: String | undefined,
  isRolledOverInvestments: String | undefined,
  isGrossReturnObjective: String | undefined,
  isTargetInvestmentSize: String | undefined,
  isTargetInvestmentSizePerTransaction: String | undefined,
  isTargetNumberInvestments: String | undefined,
  isAverageHoldingPeriod: String | undefined,
  isExitStrategy: String | undefined,
  sdInvestmentPolicy: String | undefined,
  // Deal Flow
  dfTotalDealsEvaluated: String | undefined,
  dfCurrentPipeline: String | undefined,
  dfMeetingFrequency: String | undefined,
  dfInvestigationDetails: String | undefined,
  dfExclusiveVC: String | undefined,
  dfDirectorshipsPolicy: String | undefined,
  dfDebtDirectorshipsPolicy: String | undefined,
  dfConsolidatedInfo: String | undefined,
  dfNAVFrequency: String | undefined,
  dfValuationReport: String | undefined,
  dfNAVGuidelines: String | undefined,
  dfMonitoringActivities: String | undefined,
  dfContributorTerms: String | undefined,
  dfDecisionApprovals: String | undefined,
  // dfSourcingBreakdown: String | undefined,
  dfConversionRatio: Number | undefined,
  // Others
  otExternalFirms: String | undefined,
  otMonitoringActivities: String | undefined,
  otReportsFrequency: String | undefined,
  otContributorTerms: String | undefined,
  otPlacementAgents: String | undefined,
  otDecisionApprovals: String | undefined,
  otEmployeeCost: String | undefined,
  otReportingStructure: String | undefined,
  otConsolidatedInfo: String | undefined,
  otNAVFrequency: String | undefined,
  otValuationReport: String | undefined,
  otNAVGuidelines: String | undefined,
  otConflictsOfInterest: String | undefined,
  otInternalCapital: String | undefined,
  // MIS
  msMeetingFrequency: String | undefined,
  msOrgStructure: String | undefined,
  msInvestigationDetails: String | undefined,
  msExclusiveVC: String | undefined,
  msDirectorshipsPolicy: String | undefined,
  // LP Advisory Governance and Investment Committee
  lpacDetails: String | undefined,
  lpacMemberSelectionDetails: String | undefined,
  lpacLpParticipationDetails: String | undefined,
  lpacBindingApprovalRightsDetails: String | undefined,
  lpacTotalNumberAndVotingMembersDetails: String | undefined,
  lpacDecisionMakingProcess: String | undefined,
  lpacIndependentMembersDetails: String | undefined,
  /** Average IA (self-rating) score, same as preview Average Score (total / N by manager type); set on prelim list APIs. */
  initialSelfRatingScore?: string | undefined,
  /** Whether average meets configured pass threshold for manager/fund type; set on prelim list APIs. */
  initialAssessmentPassed?: boolean | undefined,
  /** Yes = first-time fund manager, No = experienced; set on prelim list APIs. */
  iaFundManagerExperience?: string | undefined,
  /** True when a self-rating row exists for this prelim id (GET /prelims/initial-assessment-in-progress). */
  initialAssessmentLinked?: boolean | undefined,
  /** Fund name from registration: `vcf_users.company_name` (API-enriched on prelim list/get). */
  registrationAifName?: string | undefined,
  /** Email id of the user currently responsible for this application (API-enriched). */
  pendingWithEmail?: string | undefined,
  /** Applicant (created_by) email from registration; API-enriched. */
  applicantContactEmail?: string | undefined,
  applicantContactPhone?: string | undefined,
  applicantContactAddress?: string | undefined,
  applicantContactPerson?: string | undefined,
  /** Number of unread queries (queries created by others, not by current user); set on prelim list APIs. */
  unreadQueryCount?: number | undefined,
}

export const defaultIPrelimApplicationData: IPrelimApplicationData = {
  id: undefined,
  status: undefined,
  reviewedByUserId: undefined,
  assignedCheckerUserId: undefined,
  assignedMakerUserId: undefined,
  assignedManagerUserId: undefined,
  assignedUserAdminUserId: undefined,
  assignedPfUserId: undefined,
  sanctionedAmountInr: undefined,
  createdBy: undefined,
  createdByName: undefined,
  parentId: undefined,
  createdOn: undefined,
  detailedApplicationCreatedOn: undefined,
  applicationSubmissionDate: undefined,
  detailedApplicationSubmissionDate: undefined,
  updatedBy: undefined,
  updatedOn: undefined,
  scheme: undefined,
  nameOfTheFund: undefined,
  sponsor: undefined,
  investmentManager: undefined,
  fundManager: undefined,
  dealType: undefined,
  impact: undefined,
  aifCategory: undefined,
  nameOfTrustee: undefined,
  dateOfFilingWithSEBI: null,
  contributionSought: undefined,
  termOfFund: undefined,
  commitmentPeriod: undefined,
  preferredReturn: undefined,
  managementFees: undefined,
  carriedInterest: undefined,
  dealSector: undefined,
  dealSubsector: undefined,
  description: undefined,
  investmentStrategy: undefined,
  sdPvtPlacementMemorandum: undefined,
  sdImAgreement: undefined,
  sdTrustDeal: undefined,
  sdSEBICertificate: undefined,
  sdDescription: undefined,
  sdTargetCorpusDomestic: undefined,
  sdTargetCorpusOverseas: undefined,
  sdTotalTargetCorpus: undefined,
  sdGreenShoeTargetCorpusDomestic: undefined,
  sdGreenShoeTargetCorpusOverseas: undefined,
  sdGreenShoeTotalTargetCorpus: undefined,
  sdFirstClosingDomesticAmount: undefined,
  sdFirstClosingDomesticAmountDate: null,
  sdFirstClosingOverseasAmount: undefined,
  sdFirstCorpusOverseasAmountDate: undefined,
  investmentTrackRecord: undefined,
  declarationAccepted: false,
  ndaAccepted: false,
  firstClosing: false,
  hasInvestment: false,
  stage: undefined,
  detailedApplicationId: undefined,
  actionDate: undefined,
  aifCategoryType: undefined,
  targetReturnIRR: undefined,
  fundSetupCost: undefined,
  justificationForHurdleCarryInterestRate: undefined,
  hurdleCarryInterestRate: undefined,
  otherExpenses: undefined,
  // Investment Strategy
  isStrategyBasis: undefined,
  isFocusSectors: undefined,
  isComparisonPast: undefined,
  isSignificantChange: undefined,
  isSectorSituations: undefined,
  isControlsRights: undefined,
  isRightsProtections: undefined,
  isInvestmentPolicy: undefined,
  isRisksMitigation: undefined,
  isRolledOverInvestments: undefined,
  isGrossReturnObjective: undefined,
  isTargetInvestmentSize: undefined,
  isTargetInvestmentSizePerTransaction: undefined,
  isTargetNumberInvestments: undefined,
  isAverageHoldingPeriod: undefined,
  isExitStrategy: undefined,
  sdInvestmentPolicy: undefined,
  // Deal Flow
  dfTotalDealsEvaluated: undefined,
  dfCurrentPipeline: undefined,
  dfMeetingFrequency: undefined,
  dfInvestigationDetails: undefined,
  dfExclusiveVC: undefined,
  dfDirectorshipsPolicy: undefined,
  dfDebtDirectorshipsPolicy: undefined,
  dfConsolidatedInfo: undefined,
  dfNAVFrequency: undefined,
  dfValuationReport: undefined,
  dfNAVGuidelines: undefined,
  dfMonitoringActivities: undefined,
  dfContributorTerms: undefined,
  dfDecisionApprovals: undefined,

  // dfSourcingBreakdown: undefined,
  dfConversionRatio: undefined,
  // Others
  otExternalFirms: undefined,
  otMonitoringActivities: undefined,
  otReportsFrequency: undefined,
  otContributorTerms: undefined,
  otPlacementAgents: undefined,
  otDecisionApprovals: undefined,
  otEmployeeCost: undefined,
  otReportingStructure: undefined,
  otConsolidatedInfo: undefined,
  otNAVFrequency: undefined,
  otValuationReport: undefined,
  otNAVGuidelines: undefined,
  otConflictsOfInterest: undefined,
  otInternalCapital: undefined,
  // MIS
  msMeetingFrequency: undefined,
  msOrgStructure: undefined,
  msInvestigationDetails: undefined,
  msExclusiveVC: undefined,
  msDirectorshipsPolicy: undefined,
  // LP Advisory Governance and Investment Committee
  lpacDetails: undefined,
  lpacMemberSelectionDetails: undefined,
  lpacLpParticipationDetails: undefined,
  lpacBindingApprovalRightsDetails: undefined,
  lpacTotalNumberAndVotingMembersDetails: undefined,
  lpacDecisionMakingProcess: undefined,
  lpacIndependentMembersDetails: undefined,
  initialSelfRatingScore: undefined,
  initialAssessmentPassed: undefined,
  iaFundManagerExperience: undefined,
  initialAssessmentLinked: undefined,
  registrationAifName: undefined,
  pendingWithEmail: undefined,
  applicantContactEmail: undefined,
  applicantContactPhone: undefined,
  applicantContactAddress: undefined,
  applicantContactPerson: undefined,
}

export interface IApplicationData {
  id: Number | undefined,
  actionDate?: Date,
  statusComments: String | undefined,
  attachmentBucket?: String | undefined,
  attachmentName?: String | undefined,
  status: String | undefined,
}